import type { ReactElement } from 'react'
import {
  faArrowRight,
  faCalendarDays,
  faClock,
  faMugHot,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons'

import { Icon, ListCustomBullets, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '@/features/onboarding/shared/useFixedFooterOffset'
import { HostAvatars } from './HostAvatars'

const DETAILS = [
  { icon: faClock, label: '45 min' },
  { icon: faVideo, label: 'Video call' },
  { icon: faCalendarDays, label: 'Oct 26 – 30' },
]

const STEPS = [
  {
    heading: 'Connect your accounts',
    description: 'Every bank account and card you use for your practice.',
  },
  {
    heading: 'Match your statements',
    description:
      "We reconcile each month to your bank statements. You upload any we can't pull or where there's a gap.",
  },
  {
    heading: 'Sort and close',
    description:
      "Confirm what's business, categorize your transactions, and your books are caught up.",
  },
]

export const BookingView = ({
  firstName,
  onBook,
  onSkip,
}: {
  firstName: string
  onBook: () => void
  onSkip: () => void
}): ReactElement => {
  const footerLeftOffset = useFixedFooterOffset()
  return (
    <div className="flex flex-col min-h-full">
      <div className="flex-1 w-full max-w-200 mx-auto px-4 pt-6 pb-[96px] max-sm:pb-6">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MOSS}>
          Welcome to Heard, {firstName}
        </Text>
        <Text variant={TypographyStyles.DISPLAY_SM} className="mt-2">
          Get your books caught up, live with us
        </Text>
        <Text
          variant={TypographyStyles.BODY_LG}
          color={TextColors.MUTED}
          className="mt-3 max-w-170"
        >
          Join Robby and Ali on a video call. They&apos;ll walk you through
          onboarding and get your books caught up with you.
        </Text>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex items-center gap-3">
            <HostAvatars size="md" />
            <div>
              <Text variant={TypographyStyles.LABEL_MD}>Robby and Ali</Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                from the Heard team
              </Text>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {DETAILS.map(({ icon, label }) => (
              <span key={label} className="flex items-center gap-2">
                <Icon icon={icon} color="text-moss-300" />
                <Text variant={TypographyStyles.BODY_MD}>{label}</Text>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <Text variant={TypographyStyles.LABEL_LG}>On the call</Text>
          <ListCustomBullets variant="number" className="mt-4 pl-5">
            {STEPS.map((step) => (
              <ListCustomBullets.Item
                key={step.heading}
                heading={step.heading}
                description={step.description}
                numberBgColor="bg-moss-50"
                numberTextColor="text-moss-300"
              />
            ))}
          </ListCustomBullets>
        </div>

        <div className="mt-10 flex items-start gap-4 rounded-3xl bg-moss-25 p-6 max-sm:p-5">
          <Icon
            icon={faMugHot}
            color="text-moss-300"
            className="mt-1 shrink-0"
          />
          <div>
            <Text variant={TypographyStyles.LABEL_LG}>Coffee&apos;s on us</Text>
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
              className="mt-1"
            >
              Get a $15 Starbucks gift card for joining the session.
            </Text>
          </div>
        </div>
      </div>

      <OnboardingStickyNav
        leftOffset={footerLeftOffset}
        className="max-w-200 px-4"
        leading={
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.MUTED}
            className="max-sm:text-center"
          >
            Pick a time that works for you.
          </Text>
        }
        onSkip={onSkip}
        skipLabel="I'll set up on my own"
        skipVariant="link"
        onContinue={onBook}
        continueLabel="Book my session"
        continueIcon={faArrowRight}
      />
    </div>
  )
}
